import React from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import Seo from '../components/Seo';
import { companyInfo } from '../mockData';

const Section = ({ title, children }) => (
  <section className="mb-14">
    <h2 className="font-display text-3xl md:text-4xl leading-tight text-brand-oxford mb-5">{title}</h2>
    <div className="prose max-w-none text-brand-slate text-[15px] leading-relaxed space-y-4">
      {children}
    </div>
  </section>
);

const PrivacyPolicyPage = () => {
  return (
    <>
      <Seo
        title="Privacy Policy · Lockene"
        description="How Lockene collects, uses and protects information across its websites and businesses."
      />

      {/* Hero */}
      <section className="bg-brand-alabaster pt-28 md:pt-36 pb-16" aria-labelledby="pp-heading">
        <div className="container-editorial">
          <div className="border-t border-brand-hairline mb-14" />
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="hairline" aria-hidden />
              <span className="eyebrow">Legal</span>
            </div>
            <h1 id="pp-heading" className="font-display text-5xl md:text-6xl leading-[1.05] tracking-tight text-brand-oxford">
              Privacy Policy
            </h1>
            <p className="mt-6 text-lg text-brand-slate leading-relaxed">
              Last updated {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-brand-hairline py-24 md:py-28">
        <div className="container-editorial max-w-4xl">
          <Section title="Introduction">
            <p>
              Lockene (the “Company,” “we,” “our,” or “us”) is committed to protecting the privacy of its clients,
              prospects and website visitors. This Privacy Policy explains what information we collect,
              how we use it, and the safeguards we apply.
            </p>
            <p>By using our websites and services, you consent to the practices described here.</p>
          </Section>

          <Section title="Information we collect">
            <p><strong>Personal information</strong> — name, company, email, phone, and other business contact details you provide via forms or communications.</p>
            <p><strong>Server logs</strong> — IP address, browser type, referring pages and timestamps used for diagnostics and abuse prevention. Server logs are not linked to personal identifiers.</p>
            <p><strong>Cookies</strong> — session and analytics cookies used to improve the site. You may configure your browser to reject cookies.</p>
          </Section>

          <Section title="How we use information">
            <ul className="list-disc pl-6 space-y-2">
              <li>Operate, secure and improve our websites and services.</li>
              <li>Respond to enquiries, deliver mandates and communicate about ongoing engagements.</li>
              <li>Comply with contractual, statutory and audit obligations.</li>
              <li>Detect and prevent fraud and abuse.</li>
            </ul>
          </Section>

          <Section title="Data sharing">
            <p>We do not sell or trade personal information. We share information only with vetted service providers who assist in delivering our services, under contractual confidentiality obligations, or when required by law or regulator.</p>
          </Section>

          <Section title="Your rights">
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Access</strong> — request copies of your personal data.</li>
              <li><strong>Rectification</strong> — request correction of inaccurate data.</li>
              <li><strong>Erasure</strong> — request deletion, subject to legal retention obligations.</li>
              <li><strong>Withdrawal of consent</strong> — at any time, for any consent-based processing.</li>
            </ul>
          </Section>

          <Section title="Children">
            <p>Our services are not directed to individuals under 18. If you believe a minor has provided data, contact us and we will remove it.</p>
          </Section>

          <Section title="Changes">
            <p>We may update this policy from time to time. Material changes will be highlighted on this page and reflected in the “last updated” date above.</p>
          </Section>

          <Section title="Contact">
            <div className="not-prose border border-brand-hairline p-6 space-y-3 bg-brand-alabaster">
              <div className="flex items-center gap-3 text-brand-oxford">
                <Mail size={16} strokeWidth={1.5} />
                <a href={`mailto:${companyInfo.primaryEmail}`} className="hover:text-brand-gold">{companyInfo.primaryEmail}</a>
              </div>
              <div className="flex items-center gap-3 text-brand-oxford">
                <Phone size={16} strokeWidth={1.5} />
                <a href={`tel:${companyInfo.primaryPhone.replace(/\s+/g, '')}`} className="hover:text-brand-gold">{companyInfo.primaryPhone}</a>
              </div>
              <div className="flex items-start gap-3 text-brand-slate">
                <MapPin size={16} strokeWidth={1.5} className="mt-0.5" />
                <span>{companyInfo.locations.map((l) => l.city).join(' · ')}</span>
              </div>
            </div>
          </Section>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicyPage;
